import { Headset, PaperPlaneTilt, X } from "@phosphor-icons/react";
import { Card, CardContent, CardFooter, CardHeader } from "../Card";
import { Button } from "../Button";
import { Input } from "../Input";
import React, {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { ChatMessages } from "./ChatMessages";
import { ScrollArea } from "../ScrollArea";
import { cn, useIsMobile } from "../../../utils";
import { ChatbotMessage } from "@ac-expo/api-types";

type ChatbotWindowProps = {
  showWindow: boolean;
  setShowWindow: Dispatch<SetStateAction<boolean>>;
  setShowWidget: Dispatch<SetStateAction<boolean>>;
  messages: ChatbotMessage[];
  isBotWriting: boolean;
  sendMessage: (content: string) => void;
};
export const ChatbotWindow = ({
  messages,
  setShowWindow,
  showWindow,
  sendMessage,
  isBotWriting,
}: ChatbotWindowProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    document.body.style.overflow = isMobile && showWindow ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile, showWindow]);

  const close = () => setShowWindow(false);

  return (
    <Card
      className={cn(
        "bottom-20 right-6 h-[516px] w-[340px] rounded-2xl border-none bg-gray-50 p-0",
        { "fixed bottom-0 right-0 size-full rounded-none": isMobile }
      )}
      style={{
        boxShadow: "0px 12px 28px 0px #00000014, -16px 12px 68px 0px #00000026",
      }}
    >
      <CardContent className="flex size-full flex-col items-center justify-between p-0">
        <CardHeader
          className={cn(
            "flex h-[70px] w-full flex-row items-center justify-between rounded-t-2xl bg-[#01375F] p-4",
            { "rounded-none": isMobile }
          )}
        >
          <div className="flex flex-row items-center justify-center gap-3">
            <div className="flex size-[40px] items-center justify-center rounded-full bg-[#439BDD] p-0">
              <Headset size={20} weight="fill" fill="#01375F" />
            </div>
            <p className="text-white">Assistant Virtuel</p>
          </div>
          <div className="flex items-center justify-end gap-2">
            <HeaderButton onClick={close}>
              <X size={16} weight="bold" fill="#FFFFFF" />
            </HeaderButton>
          </div>
        </CardHeader>
        <div className="flex size-full max-h-[calc(100%-146px)] flex-col justify-end">
          <ScrollArea className="px-4 py-2">
            <div className="flex flex-col justify-end gap-2">
              <ChatMessages messages={messages} />
              {isBotWriting ? (
                <p className="text-xs text-gray-500">
                  L&apos;assistant est en train d&apos;écrire...
                </p>
              ) : null}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>
        </div>
        <CardFooter className="flex h-[76px] w-full flex-row items-center justify-between gap-2 rounded-b-2xl border-t bg-white px-6 py-4">
          <ChatTyper submit={sendMessage} />
        </CardFooter>
      </CardContent>
    </Card>
  );
};

type HeaderButtonProps = {
  onClick?: () => void;
};
const HeaderButton: FC<PropsWithChildren<HeaderButtonProps>> = ({
  children,
  onClick,
}) => {
  return (
    <Button
      onClick={onClick}
      variant="null"
      className="size-8 rounded-md bg-[#FFFFFF4D] p-1"
    >
      {children}
    </Button>
  );
};

type ChatTyperProps = {
  submit: (value: string) => void;
};
const ChatTyper = ({ submit }: ChatTyperProps) => {
  const [value, setValue] = useState("");

  const onSubmit = useCallback(() => {
    if (!value) {
      return;
    }
    submit(value);
    setValue("");
  }, [value, submit]);

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        onSubmit();
      }
    };
    window.addEventListener("keydown", listener);

    return () => {
      window.removeEventListener("keydown", listener);
    };
  }, [value, onSubmit]);

  return (
    <>
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="size-full rounded-lg placeholder:text-gray-500"
        placeholder="Posez moi une question..."
      />
      <Button
        variant="null"
        className="p-0"
        onClick={onSubmit}
        id="chat-bot-send"
      >
        <PaperPlaneTilt size={21} weight="fill" fill="#287BB9" />
      </Button>
    </>
  );
};
